import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { useVirtualizer } from '@tanstack/react-virtual'

import { type ChatId, useSelectedChatKey } from '../../blocs/chatVisibility'
import { ChatMessage } from '../ChatMessage/ChatMessage'
import {
  getNextPage,
  isFetchingNextPage,
  useChatMessages,
  useHasNextPage,
} from '../../blocs/useChat'

import './ChatMessages.scss'

interface ChatMessagesProps {
  type: ChatId
}

const SCROLL_END_THRESHOLD = 200
const MESSAGE_GAP = 12
const EMPTY_MESSAGE_IDS: readonly (number | undefined)[] = []

const ChatMessages = ({ type }: ChatMessagesProps) => {
  const chatMessageIds = useChatMessages(type)
  const canFetchNextPage = useHasNextPage(type)

  const scrollContainer = useRef<HTMLDivElement>(null)

  const isHidden = useSelectedChatKey() !== type

  const messageIds = chatMessageIds ?? EMPTY_MESSAGE_IDS

  const virtualizer = useVirtualizer({
    count: messageIds.length,
    getScrollElement: () => scrollContainer.current,
    estimateSize: () => 56,
    overscan: 3,
    gap: MESSAGE_GAP,
    getItemKey: (index) => messageIds[index] ?? `placeholder-${index}`,
    anchorTo: 'end',
    followOnAppend: 'smooth',
    scrollEndThreshold: SCROLL_END_THRESHOLD,
  })

  const virtualItems = virtualizer.getVirtualItems()
  const firstIndex = virtualItems[0]?.index
  const lastIndex = virtualItems[virtualItems.length - 1]?.index

  const hasScrolledToEnd = useRef(false)

  const isLastMessageLoaded = messageIds[messageIds.length - 1] !== undefined

  useLayoutEffect(() => {
    if (hasScrolledToEnd.current || isHidden || !isLastMessageLoaded) return
    hasScrolledToEnd.current = true
    virtualizer.scrollToEnd({ behavior: 'auto' })
  }, [isHidden, isLastMessageLoaded, virtualizer])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (firstIndex === undefined || lastIndex === undefined) return
    if (!canFetchNextPage || isFetchingNextPage(type)) return

    // a placeholder scrolled into view means the page holding it isn't loaded
    if (
      messageIds[firstIndex] === undefined ||
      messageIds[lastIndex] === undefined
    )
      void getNextPage(type)
  }, [canFetchNextPage, messageIds, type, firstIndex, lastIndex])

  if (chatMessageIds?.length === 0) {
    return (
      <div className="webinar-chat-no-messages" data-hidden={isHidden}>
        <FormattedMessage defaultMessage="No chat messages have been posted" />
      </div>
    )
  }

  return (
    <div
      ref={scrollContainer}
      className="webinar-chat-messages"
      data-hidden={isHidden}
    >
      <div
        className="webinar-chat-messages-list"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualItems.map((virtualItem) => (
          <div
            key={String(virtualItem.key)}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
            className="webinar-chat-messages-item"
            style={{ top: virtualItem.start }}
          >
            <ChatMessage
              hash={virtualItem.index}
              id={messageIds[virtualItem.index]}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
export { ChatMessages }
